/**
 * The interviewer card's own edits, as pure functions.
 *
 * Toggling a section, stepping its minutes, dragging it, cycling a skill:
 * these change the config on the page at once, with no request, because the
 * recruiter is shaping a draft. Rio validates the whole config again on save.
 * Plain-language edits are different: those go to Rio (see useInterviewer).
 *
 * The total length is the recruiter's number. Sections share it, so turning one
 * off hands its minutes to the others, and moving the length slider rescales
 * them all. Stepping one section's minutes is the one edit that changes the
 * total, within 15 to 45 minutes.
 */
import type {
  CustomQuestion,
  DiffEntry,
  ExpectationLevel,
  QuestionExpectations,
  InterviewerConfig,
  InterviewerPreview,
  InterviewerSection,
  PostingSkill,
  PreviewSection,
  SectionKey,
  Tone,
  WorkspaceKind,
} from '../services/interviewer.types';

export const MIN_TOTAL = 15;
export const MAX_TOTAL = 45;
export const MIN_SECTION = 1;
export const MAX_SECTION = 30;
export const MAX_QUESTIONS = 5;
export const MAX_QUESTION_LENGTH = 300;
export const MAX_AVOID_TOPICS = 10;
export const MAX_AVOID_TOPIC_LENGTH = 80;
/** Rio takes at most five skills of more focus, and five of less. */
export const MAX_FOCUS = 5;

/** Titles and one-line summaries, used until Rio's preview supplies its own. */
export const SECTION_META: Record<
  SectionKey,
  { title: string; summary: string }
> = {
  logistics: {
    title: 'The practical side',
    summary:
      'Location, schedule, pay and work authorisation, so nothing surprises anyone later.',
  },
  skills: {
    title: 'Skills and experience',
    summary:
      "Real situations from the role, aimed at what the résumé doesn't show.",
  },
  workspace: {
    title: 'Work task',
    summary:
      'A short task from the job, worked with an AI assistant beside them.',
  },
  ai_fluency: {
    title: 'How they work with AI',
    summary:
      'What they hand to AI, how they check it and what they keep for themselves.',
  },
  candidate_questions: {
    title: 'Their questions',
    summary:
      'Rio answers from the posting and flags anything it can’t answer for you.',
  },
};

export const enabledTotal = (sections: InterviewerSection[]): number =>
  sections.filter((s) => s.enabled).reduce((sum, s) => sum + s.minutes, 0);

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

/**
 * Shares `target` minutes across the enabled sections in proportion to their
 * current minutes, as whole minutes that add up exactly (largest remainder),
 * each at least one minute. Disabled sections keep their minutes, so turning
 * one back on restores roughly what it had.
 */
export const rebalance = (
  sections: InterviewerSection[],
  target: number,
): InterviewerSection[] => {
  const enabled = sections.filter((s) => s.enabled);
  if (!enabled.length) return sections;
  const weights = enabled.map((s) => Math.max(s.minutes, MIN_SECTION));
  const weightTotal = weights.reduce((a, b) => a + b, 0);
  const spare = target - enabled.length * MIN_SECTION;
  const exact = weights.map((w) => (Math.max(spare, 0) * w) / weightTotal);
  const whole = exact.map(Math.floor);
  let left = Math.max(spare, 0) - whole.reduce((a, b) => a + b, 0);
  const byRemainder = exact
    .map((value, index) => ({ index, rest: value - Math.floor(value) }))
    .sort((a, b) => b.rest - a.rest);
  for (const { index } of byRemainder) {
    if (left <= 0) break;
    whole[index] += 1;
    left -= 1;
  }
  let i = 0;
  return sections.map((s) =>
    s.enabled ? { ...s, minutes: MIN_SECTION + whole[i++] } : s,
  );
};

export const setTotal = (
  config: InterviewerConfig,
  minutes: number,
): InterviewerConfig => {
  const target = clamp(Math.round(minutes), MIN_TOTAL, MAX_TOTAL);
  return {
    ...config,
    targetMinutes: target,
    sections: rebalance(config.sections, target),
  };
};

/** False for the last section still on: an interview needs at least one. */
/**
 * Om's decision (27 Oct): every candidate gets to ask their own questions, and
 * every interview covers the role's skills. Rio rejects a config that turns
 * either off. The candidate's questions also stay last.
 */
export const ALWAYS_ON: ReadonlySet<SectionKey> = new Set<SectionKey>([
  'skills',
  'candidate_questions',
]);
export const FIXED_LAST: SectionKey = 'candidate_questions';

/** The always-on sections switched on, and the candidate's questions last. */
export const pinSections = (
  sections: InterviewerSection[],
): InterviewerSection[] => {
  const on = sections.map((s) =>
    ALWAYS_ON.has(s.key) && !s.enabled ? { ...s, enabled: true } : s,
  );
  const last = on.find((s) => s.key === FIXED_LAST);
  return last ? [...on.filter((s) => s.key !== FIXED_LAST), last] : on;
};

/** The last position a section can be dragged to: above the candidate's questions. */
export const lastMovableIndex = (config: InterviewerConfig) =>
  config.sections[config.sections.length - 1]?.key === FIXED_LAST
    ? config.sections.length - 2
    : config.sections.length - 1;

export const canToggleOff = (config: InterviewerConfig, key: SectionKey) =>
  !ALWAYS_ON.has(key) &&
  config.sections.filter((s) => s.enabled && s.key !== key).length > 0;

export const toggleSection = (
  config: InterviewerConfig,
  key: SectionKey,
): InterviewerConfig => {
  const section = config.sections.find((s) => s.key === key);
  if (!section) return config;
  if (section.enabled && !canToggleOff(config, key)) return config;
  const sections = config.sections.map((s) =>
    s.key === key ? { ...s, enabled: !s.enabled, minutes: s.minutes || 5 } : s,
  );
  return {
    ...config,
    sections: rebalance(sections, config.targetMinutes),
    workspace:
      key === 'workspace'
        ? { ...config.workspace, enabled: !section.enabled }
        : config.workspace,
  };
};

export const canStep = (
  config: InterviewerConfig,
  key: SectionKey,
  delta: number,
): boolean => {
  const section = config.sections.find((s) => s.key === key);
  if (!section?.enabled) return false;
  const next = section.minutes + delta;
  const total = enabledTotal(config.sections) + delta;
  return (
    next >= MIN_SECTION &&
    next <= MAX_SECTION &&
    total >= MIN_TOTAL &&
    total <= MAX_TOTAL
  );
};

export const stepSection = (
  config: InterviewerConfig,
  key: SectionKey,
  delta: number,
): InterviewerConfig => {
  if (!canStep(config, key, delta)) return config;
  const sections = config.sections.map((s) =>
    s.key === key ? { ...s, minutes: s.minutes + delta } : s,
  );
  return { ...config, sections, targetMinutes: enabledTotal(sections) };
};

export const moveSection = (
  config: InterviewerConfig,
  from: number,
  to: number,
): InterviewerConfig => {
  const last = lastMovableIndex(config);
  const target = clamp(to, 0, last);
  if (from === target || from < 0 || from > last) return config;
  const sections = [...config.sections];
  const [moved] = sections.splice(from, 1);
  sections.splice(target, 0, moved);
  return { ...config, sections };
};

export type SkillFocus = 'neutral' | 'more' | 'less';

export const skillFocus = (
  config: InterviewerConfig,
  key: string,
): SkillFocus =>
  config.emphasize.includes(key)
    ? 'more'
    : config.deemphasize.includes(key)
      ? 'less'
      : 'neutral';

/** Neutral, then more focus, then less focus, then back to neutral. */
export const cycleSkill = (
  config: InterviewerConfig,
  key: string,
): InterviewerConfig => {
  const current = skillFocus(config, key);
  const emphasize = config.emphasize.filter((k) => k !== key);
  const deemphasize = config.deemphasize.filter((k) => k !== key);
  // A state already holding five is skipped, never overfilled.
  const roomMore = emphasize.length < MAX_FOCUS;
  const roomLess = deemphasize.length < MAX_FOCUS;
  if (current === 'neutral') {
    if (roomMore) emphasize.push(key);
    else if (roomLess) deemphasize.push(key);
  }
  if (current === 'more' && roomLess) deemphasize.push(key);
  return { ...config, emphasize, deemphasize };
};

let questionCounter = 0;
export const newQuestionId = () =>
  `q-${Date.now().toString(36)}-${(questionCounter += 1)}`;

export const addQuestion = (
  config: InterviewerConfig,
  text: string,
  section: SectionKey,
  id: string = newQuestionId(),
): InterviewerConfig => {
  const clean = text.trim().slice(0, MAX_QUESTION_LENGTH);
  if (!clean || config.customQuestions.length >= MAX_QUESTIONS) return config;
  // Rio sketches the bar (area and expectations) once it sees the question;
  // until then they're empty, and the question counts by default.
  const question: CustomQuestion = {
    id,
    text: clean,
    section,
    followUp: true,
    area: null,
    areaLabel: null,
    expectations: null,
    counts: true,
  };
  return { ...config, customQuestions: [...config.customQuestions, question] };
};

export const MAX_SIGNALS = 5;
export const MAX_SIGNAL_LENGTH = 160;

const cleanSignals = (items: string[]) =>
  items
    .map((t) => t.trim().slice(0, MAX_SIGNAL_LENGTH))
    .filter(Boolean)
    .slice(0, MAX_SIGNALS);

export const updateQuestion = (
  config: InterviewerConfig,
  id: string,
  patch: Partial<
    Pick<CustomQuestion, 'text' | 'section' | 'area' | 'areaLabel' | 'counts'>
  >,
): InterviewerConfig => {
  const text =
    patch.text === undefined
      ? undefined
      : patch.text.trim().slice(0, MAX_QUESTION_LENGTH);
  if (text === '') return config;
  return {
    ...config,
    customQuestions: config.customQuestions.map((q) =>
      q.id === id
        ? {
            ...q,
            ...(text === undefined ? {} : { text }),
            ...(patch.section ? { section: patch.section } : {}),
            ...(patch.area !== undefined
              ? { area: patch.area, areaLabel: patch.areaLabel ?? null }
              : {}),
            ...(patch.counts !== undefined ? { counts: patch.counts } : {}),
          }
        : q,
    ),
  };
};

/** The bar Rio sketched, or one the recruiter edited: a level's signals replaced. */
export const setExpectationLevel = (
  config: InterviewerConfig,
  id: string,
  level: ExpectationLevel,
  items: string[],
): InterviewerConfig => ({
  ...config,
  customQuestions: config.customQuestions.map((q) =>
    q.id === id
      ? {
          ...q,
          expectations: {
            excellent: [],
            adequate: [],
            weak: [],
            ...q.expectations,
            [level]: cleanSignals(items),
          },
        }
      : q,
  ),
});

/** Rio's whole sketch for a question, when it arrives. */
export const setExpectations = (
  config: InterviewerConfig,
  id: string,
  expectations: QuestionExpectations,
  area: { key: string; label: string } | null,
): InterviewerConfig => ({
  ...config,
  customQuestions: config.customQuestions.map((q) =>
    q.id === id
      ? {
          ...q,
          expectations: {
            excellent: cleanSignals(expectations.excellent),
            adequate: cleanSignals(expectations.adequate),
            weak: cleanSignals(expectations.weak),
          },
          area: area?.key ?? q.area,
          areaLabel: area?.label ?? q.areaLabel,
        }
      : q,
  ),
});

export const removeQuestion = (
  config: InterviewerConfig,
  id: string,
): InterviewerConfig => ({
  ...config,
  customQuestions: config.customQuestions.filter((q) => q.id !== id),
});

export const addAvoidTopic = (
  config: InterviewerConfig,
  topic: string,
): InterviewerConfig => {
  const clean = topic.trim().slice(0, MAX_AVOID_TOPIC_LENGTH);
  if (
    !clean ||
    config.avoidTopics.length >= MAX_AVOID_TOPICS ||
    config.avoidTopics.some((t) => t.toLowerCase() === clean.toLowerCase())
  ) {
    return config;
  }
  return { ...config, avoidTopics: [...config.avoidTopics, clean] };
};

export const removeAvoidTopic = (
  config: InterviewerConfig,
  topic: string,
): InterviewerConfig => ({
  ...config,
  avoidTopics: config.avoidTopics.filter((t) => t !== topic),
});

export const setTone = (
  config: InterviewerConfig,
  tone: Tone,
): InterviewerConfig => ({
  ...config,
  tone,
});

export const setWorkspace = (
  config: InterviewerConfig,
  patch: Partial<InterviewerConfig['workspace']>,
): InterviewerConfig => {
  const workspace = { ...config.workspace, ...patch };
  const hasSection = config.sections.some((s) => s.key === 'workspace');
  const sectionOn = config.sections.find((s) => s.key === 'workspace')?.enabled;
  const next = { ...config, workspace };
  // The work task is also a section of the interview; keep the two in step.
  if (
    hasSection &&
    patch.enabled !== undefined &&
    patch.enabled !== sectionOn
  ) {
    const toggled = toggleSection(next, 'workspace');
    return { ...toggled, workspace };
  }
  return next;
};

export const WORKSPACE_LABELS: Record<WorkspaceKind, string> = {
  auto: 'Rio picks',
  code: 'Code',
  whiteboard: 'Whiteboard',
  design: 'Design canvas',
};

/**
 * The candidate's-eye preview for the config on the page. Rio's last preview
 * supplies the titles, summaries and example questions; the order, minutes,
 * switches and the recruiter's own questions come from the config, so the
 * preview follows every local edit at once without a request.
 */
export const derivePreview = (
  config: InterviewerConfig,
  base: InterviewerPreview | null,
  skills: PostingSkill[],
): InterviewerPreview => {
  const byKey = new Map(base?.sections.map((s) => [s.key, s]) ?? []);
  const sections: PreviewSection[] = config.sections.map((s) => {
    const fromRio = byKey.get(s.key);
    return {
      key: s.key,
      title: fromRio?.title || SECTION_META[s.key].title,
      summary: fromRio?.summary || SECTION_META[s.key].summary,
      exampleQuestions: fromRio?.exampleQuestions ?? [],
      minutes: s.minutes,
      enabled: s.enabled,
    };
  });
  const label = (key: string) =>
    skills.find((s) => s.key === key)?.label ??
    base?.emphasis.emphasize.find((s) => s.key === key)?.label ??
    base?.emphasis.deemphasize.find((s) => s.key === key)?.label ??
    key;
  return {
    totalMinutes: enabledTotal(config.sections),
    sections,
    workspace: {
      enabled: config.workspace.enabled,
      kind: config.workspace.kind,
      label:
        base?.workspace.kind === config.workspace.kind
          ? base.workspace.label
          : WORKSPACE_LABELS[config.workspace.kind],
      note:
        config.workspace.kind === 'whiteboard' ||
        config.workspace.kind === 'design'
          ? (base?.workspace.kind === config.workspace.kind &&
              base.workspace.note) ||
            'Coming soon. Until then Rio uses a code task, or none if the role has no code.'
          : null,
    },
    emphasis: {
      emphasize: config.emphasize.map((key) => ({ key, label: label(key) })),
      deemphasize: config.deemphasize.map((key) => ({
        key,
        label: label(key),
      })),
    },
    customQuestions: config.customQuestions.map(
      ({ id, text, section, area, areaLabel, expectations, counts }) => ({
        id,
        text,
        section,
        area,
        areaLabel,
        expectations,
        counts,
      }),
    ),
    notes: base?.notes ?? [],
    areas: base?.areas ?? [],
  };
};

/**
 * Which parts of the card a plain-language edit changed, so they can light up
 * briefly. Keys match the `data-region` names the card puts on each part.
 */
export const changedRegions = (
  diff: DiffEntry[],
  config: InterviewerConfig,
): Set<string> => {
  const regions = new Set<string>();
  for (const { path, before, after } of diff) {
    const sectionMatch = /^sections\[(\d+)\]/.exec(path);
    if (sectionMatch) {
      const section = config.sections[Number(sectionMatch[1])];
      if (section) regions.add(`section:${section.key}`);
      regions.add('sections');
      continue;
    }
    if (path === 'sections') {
      regions.add('sections');
      continue;
    }
    if (path.startsWith('targetMinutes')) regions.add('length');
    else if (path.startsWith('emphasize') || path.startsWith('deemphasize')) {
      regions.add('skills');
      const keys = new Set([
        ...(Array.isArray(before) ? before : []),
        ...(Array.isArray(after) ? after : []),
      ]);
      const was = new Set(Array.isArray(before) ? before : []);
      const now = new Set(Array.isArray(after) ? after : []);
      keys.forEach((k) => {
        if (typeof k === 'string' && was.has(k) !== now.has(k))
          regions.add(`skill:${k}`);
      });
    } else if (path.startsWith('customQuestions')) regions.add('questions');
    else if (path.startsWith('avoidTopics')) regions.add('avoid');
    else if (path.startsWith('tone')) regions.add('tone');
    else if (path.startsWith('workspace')) regions.add('workspace');
  }
  return regions;
};

export const sameConfig = (
  a: InterviewerConfig | null,
  b: InterviewerConfig | null,
) => {
  if (!a || !b) return a === b;
  const strip = (c: InterviewerConfig) => ({ ...c, meta: null });
  return JSON.stringify(strip(a)) === JSON.stringify(strip(b));
};

const plural = (n: number, one: string, many: string) =>
  `${n} ${n === 1 ? one : many}`;

/**
 * One line saying what a saved version changed against the one before, for
 * the version history. Rio sends the configs; the words are worked out here.
 */
export const summarizeChange = (
  before: InterviewerConfig | null,
  after: InterviewerConfig,
): string => {
  if (!before) return 'Started from Rio’s draft';
  const parts: string[] = [];
  const title = (key: SectionKey) => SECTION_META[key].title;
  if (before.targetMinutes !== after.targetMinutes) {
    parts.push(`length to ${after.targetMinutes} minutes`);
  }
  const was = new Map(before.sections.map((s) => [s.key, s]));
  after.sections.forEach((s) => {
    const prev = was.get(s.key);
    if (prev && prev.enabled !== s.enabled) {
      parts.push(`${s.enabled ? 'added' : 'skipped'} ${title(s.key)}`);
    }
  });
  const order = (c: InterviewerConfig) => c.sections.map((s) => s.key).join();
  if (order(before) !== order(after)) parts.push('reordered the sections');
  const focus = (c: InterviewerConfig) =>
    [...c.emphasize, '|', ...c.deemphasize].join();
  if (focus(before) !== focus(after)) parts.push('changed skill focus');
  const added = after.customQuestions.length - before.customQuestions.length;
  if (added > 0) parts.push(`added ${plural(added, 'question', 'questions')}`);
  else if (added < 0)
    parts.push(`removed ${plural(-added, 'question', 'questions')}`);
  else if (
    JSON.stringify(before.customQuestions) !==
    JSON.stringify(after.customQuestions)
  ) {
    parts.push('edited questions');
  }
  if (before.tone !== after.tone) parts.push(`tone to ${after.tone}`);
  if (JSON.stringify(before.workspace) !== JSON.stringify(after.workspace)) {
    parts.push(
      after.workspace.enabled
        ? 'changed the work task'
        : 'removed the work task',
    );
  }
  if (before.avoidTopics.join() !== after.avoidTopics.join())
    parts.push('changed topics to avoid');
  if (!parts.length) return 'Saved with no changes';
  const line = parts.join('; ');
  return line.charAt(0).toUpperCase() + line.slice(1);
};

export const skillKey = (label: string) =>
  label
    .toLowerCase()
    .replace(/[^a-z0-9+#.]+/g, '-')
    .replace(/^-+|-+$/g, '');

const ENTITIES: Record<string, string> = {
  '&nbsp;': ' ',
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
};

/** The editor writes HTML; Rio drafts from the words. */
export const stripHtml = (html: string) =>
  html
    .replace(/<(br|\/p|\/li|\/h\d)[^>]*>/gi, '\n')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&(nbsp|amp|lt|gt|quot|#39);/g, (m) => ENTITIES[m] ?? m)
    .replace(/[ \t]+/g, ' ')
    .replace(/\s*\n\s*/g, '\n')
    .trim();

export const postingFrom = ({
  title,
  description,
  requiredSkills,
  preferredSkills,
}: {
  title?: string | null;
  description?: string | null;
  requiredSkills?: string[] | null;
  preferredSkills?: string[] | null;
}) => {
  const skills: PostingSkill[] = [];
  const seen = new Set<string>();
  const add = (labels: string[] | null | undefined, required: boolean) =>
    (labels ?? []).forEach((raw) => {
      const label = String(raw).trim();
      const key = skillKey(label);
      if (!label || !key || seen.has(key)) return;
      seen.add(key);
      skills.push({ key, label, required });
    });
  add(requiredSkills, true);
  add(preferredSkills, false);
  return {
    title: (title ?? '').trim(),
    description: stripHtml(description ?? ''),
    skills,
  };
};

/** Enough of a posting for Rio to draft something worth showing. */
export const postingReady = (posting: { title: string; description: string }) =>
  posting.title.length > 0 && posting.description.length >= 40;
